import {
  useState,
  useEffect,
  useContext,
  memo,
  useRef,
  useCallback,
} from 'react';
import { ISideNavItem } from '../../types';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '../../utils';
import { SidebarNavigationContext } from '../../contexts';
import { NavLink, useLocation } from 'react-router-dom';

const subMenuVariants = {
  close: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.1 },
  },
  open: {
    opacity: 1,
    height: 'auto',
    transition: { duration: 0.1 },
  },
} as const;

interface MenuItemProps {
  item: ISideNavItem;
}

const MenuItem: React.FC<MenuItemProps> = memo(function MenuItem({ item }) {
  const { isOpen: sidebarExpanded } = useContext(SidebarNavigationContext);
  const location = useLocation();
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const hasInitialized = useRef(false);

  const isActive = item.subMenuItems
    ? item.subMenuItems.some(subItem => location.pathname === subItem.path)
    : location.pathname === item.path;

  // Auto-open submenu only on initial mount if it contains active route
  useEffect(() => {
    if (!hasInitialized.current && item.subMenuItems) {
      const hasActiveSubItem = item.subMenuItems.some(
        subItem => location.pathname === subItem.path,
      );
      if (hasActiveSubItem) {
        setSubMenuOpen(true);
      }
      hasInitialized.current = true;
    }
  }, [location.pathname, item.subMenuItems]);

  const toggleSubMenu = useCallback(() => {
    setSubMenuOpen(prev => !prev);
  }, []);

  // Collapsed state - icon only with tooltip
  if (!sidebarExpanded) {
    const path = item.subMenu ? item.subMenuItems?.[0]?.path : item.path;
    return (
      <NavLink
        to={path || '/'}
        className={cn(
          'group relative flex items-center justify-center w-6 h-6 mx-auto rounded transition-colors',
          {
            'bg-slate-700 text-white': isActive,
            'text-slate-300 hover:bg-slate-800 hover:text-white': !isActive,
          },
        )}
        title={item.title}
        role="menuitem"
        aria-label={item.title}
      >
        <span
          className="[&>svg]:size-4 [&>svg]:stroke-current [&>svg]:text-current"
          aria-hidden="true"
        >
          {item.icon}
        </span>
        {/* Tooltip */}
        <span
          className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible whitespace-nowrap z-50 shadow-lg border border-slate-700 pointer-events-none"
          role="tooltip"
        >
          {item.title}
        </span>
      </NavLink>
    );
  }

  // Expanded state with submenu
  if (item.subMenu && item.subMenuItems) {
    return (
      <div role="none">
        <button
          onClick={toggleSubMenu}
          className={cn(
            'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
            {
              'bg-slate-700/50 text-white': isActive,
              'text-slate-300 hover:bg-slate-800 hover:text-white': !isActive,
            },
          )}
          aria-expanded={subMenuOpen}
          aria-haspopup="true"
          role="menuitem"
        >
          {item.icon && (
            <span
              className="[&>svg]:size-4 [&>svg]:stroke-current [&>svg]:text-current shrink-0 text-slate-400"
              aria-hidden="true"
            >
              {item.icon}
            </span>
          )}
          <span className="flex-1 text-left truncate">{item.title}</span>
          <ChevronRightIcon
            className={cn(
              'size-3 text-slate-500 transition-transform duration-100',
              {
                'rotate-90': subMenuOpen,
              },
            )}
            aria-hidden="true"
          />
        </button>
        <AnimatePresence initial={false}>
          {subMenuOpen && (
            <motion.ul
              initial="close"
              animate="open"
              exit="close"
              variants={subMenuVariants}
              className="mt-0.5 ml-2 pl-2 border-l border-slate-700 space-y-0.5 overflow-hidden"
              role="menu"
              aria-label={`${item.title} submenu`}
            >
              {item.subMenuItems.map((subItem, idx) => {
                const isSubItemActive = location.pathname === subItem.path;
                return (
                  <li key={subItem.path || idx} role="none">
                    <NavLink
                      to={subItem.path}
                      className={cn(
                        'block px-2 py-1 rounded-md text-xs transition-colors truncate',
                        {
                          'bg-slate-700 text-white font-medium':
                            isSubItemActive,
                          'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50':
                            !isSubItemActive,
                        },
                      )}
                      role="menuitem"
                      aria-current={isSubItemActive ? 'page' : undefined}
                    >
                      {subItem.title}
                    </NavLink>
                  </li>
                );
              })}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Expanded state without submenu
  return (
    <NavLink
      to={item.path || '/'}
      className={({ isActive: navIsActive }) =>
        cn(
          'flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors truncate',
          {
            'bg-slate-700 text-white': navIsActive,
            'text-slate-300 hover:bg-slate-800 hover:text-white': !navIsActive,
          },
        )
      }
      role="menuitem"
      aria-current={isActive ? 'page' : undefined}
    >
      {item.icon && (
        <span
          className="[&>svg]:size-4 [&>svg]:stroke-current [&>svg]:text-current shrink-0 text-slate-400"
          aria-hidden="true"
        >
          {item.icon}
        </span>
      )}
      <span className="truncate">{item.title}</span>
    </NavLink>
  );
});

export default MenuItem;
